import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import { Spinner } from "react-bootstrap";
import AdminLayout from "../../layouts/admin";
import { getOnlineUsers } from "Services/user-analytics";

import "./index.scss";

type OnlineUser = {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  company?: string | null;
  current_page?: string;
  current_page_title?: string;
  online_since?: string | null;
  recent_activity?: string | null;
  connected_seconds: number;
};

const REFRESH_INTERVAL = 30 * 1000;

const formatDuration = (totalSeconds: number) => {
  const seconds = Math.max(
    0,
    Math.floor(totalSeconds || 0)
  );

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(
    (seconds % 3600) / 60
  );

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m`;
  }

  return "< 1m";
};

const formatLastActivity = (
  activityDate?: string | null
) => {
  if (!activityDate) {
    return "-";
  }

  const difference = Math.max(
    0,
    Math.floor(
      (Date.now() -
        new Date(activityDate).getTime()) /
        1000
    )
  );

  if (difference < 60) {
    return "Just now";
  }

  const minutes = Math.floor(difference / 60);

  if (minutes === 1) {
    return "1 minute ago";
  }

  return `${minutes} minutes ago`;
};

const LiveUsers = () => {
  const [users, setUsers] = useState<OnlineUser[]>(
    []
  );

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] =
    useState(false);
  const [error, setError] = useState("");

  const loadOnlineUsers = useCallback(
    async (manualRefresh = false) => {
      try {
        if (manualRefresh) {
          setRefreshing(true);
        }

        const response = await getOnlineUsers();

        setUsers(response?.data?.results || []);
        setError("");
      } catch (requestError) {
        console.error(
          "Failed to load online users",
          requestError
        );

        setError("Could not load connected users.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadOnlineUsers();

    const refreshTimer = window.setInterval(() => {
      loadOnlineUsers();
    }, REFRESH_INTERVAL);

    return () => {
      window.clearInterval(refreshTimer);
    };
  }, [loadOnlineUsers]);

  return (
    <AdminLayout>
      <div className="live-users-page">
        <div className="live-users-header">
          <div>
            <div className="live-users-title-row">
              <h1>Live Users</h1>

              <span className="live-users-count">
                <i aria-hidden="true" />
                {users.length} connected
              </span>
            </div>

            <p>
              Users active during the last 15
              minutes
            </p>
          </div>

          <button
            type="button"
            className="live-users-refresh"
            disabled={refreshing}
            onClick={() => loadOnlineUsers(true)}
          >
            <span
              className={
                refreshing
                  ? "live-users-refresh-icon live-users-refresh-icon--active"
                  : "live-users-refresh-icon"
              }
              aria-hidden="true"
            >
              ↻
            </span>

            Refresh
          </button>
        </div>

        {error && (
          <div className="live-users-error">
            {error}
          </div>
        )}

        <div className="live-users-table-shell">
          {loading ? (
            <div className="live-users-loader">
              <Spinner animation="border" />
            </div>
          ) : users.length === 0 ? (
            <div className="live-users-empty">
              <span className="live-users-empty-icon">
                ◌
              </span>

              <strong>
                No connected users right now
              </strong>

              <p>
                Users will appear after activity
                on the website.
              </p>
            </div>
          ) : (
            <table className="live-users-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Company</th>
                  <th>Current page</th>
                  <th>Connected for</th>
                  <th>Last activity</th>
                </tr>
              </thead>

              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <span className="live-users-online">
                        <i aria-hidden="true" />
                        Online
                      </span>
                    </td>

                    <td>
                      <div className="live-users-person">
                        <span className="live-users-avatar">
                          {(user.name ||
                            user.email ||
                            "?")
                            .charAt(0)
                            .toUpperCase()}
                        </span>

                        <div>
                          <strong>
                            {user.name || "-"}
                          </strong>

                          <small>
                            ID: {user.id}
                          </small>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="live-users-contact">
                        <a
                          href={`mailto:${user.email}`}
                        >
                          {user.email}
                        </a>

                        <span>
                          {user.phone || "-"}
                        </span>
                      </div>
                    </td>

                    <td>
                      {user.company || "-"}
                    </td>

                    <td>
                      <div className="live-users-page-cell">
                        <strong>
                          {user.current_page_title ||
                            "EasyBar"}
                        </strong>

                        <code>
                          {user.current_page || "/"}
                        </code>
                      </div>
                    </td>

                    <td>
                      <span className="live-users-duration">
                        {formatDuration(
                          user.connected_seconds
                        )}
                      </span>
                    </td>

                    <td>
                      {formatLastActivity(
                        user.recent_activity
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="live-users-footer-note">
          The list refreshes automatically every 30
          seconds. Users are removed after 15 minutes
          without activity.
        </div>
      </div>
    </AdminLayout>
  );
};

export default LiveUsers;